import * as React from 'react';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

import BarChartProgress from 'components/graphs/bar-chart-progress/BarChartProgress';
import PieChart from 'components/graphs/echarts/pie-chart/PieChart';
import Spinner from 'components/spinner/Spinner';

import { __t, applyTranslations } from 'utils/language';
import { COLORS, getReportingPieChartStyles } from 'utils/reporting';
import {
  preparePieChartData
} from 'utils/charts/pie-chart';
import { getMetricTypeWidth } from 'modules/reporting/utils/reporting-task-types';

import './zalando-outfits-progress-widget.scss';

applyTranslations(require('./zalando-outfits-progress-widget.i18n.json'));

import type {
  BarChartProgressItem,
  ChartFormatterParams,
  PieChart as PieChartAlias
  // StatisticsCommon
} from 'types';
import type {
  ReportingKPIData,
  ReportingOutfitsProgressCounts
} from 'modules/reporting/types';

interface Props {
  count: Partial<ReportingOutfitsProgressCounts>,
  tasks: ReportingKPIData | null,
  isLoading: boolean
}

const LABEL_TO_COLOR_MAP = {
  [__t('zalando_outfits_progress_widget.statuses.unpublished')]: COLORS.zalando_outfit.unpublished,
  [__t('zalando_outfits_progress_widget.statuses.approved')]: COLORS.zalando_outfit.approved,
  [__t('zalando_outfits_progress_widget.statuses.offline')]: COLORS.zalando_outfit.offline,
  [__t('zalando_outfits_progress_widget.statuses.pending')]: COLORS.zalando_outfit.review,
  [__t('zalando_outfits_progress_widget.statuses.published')]: COLORS.zalando_outfit.main
};

const getBarChartData = (tasks: ReportingKPIData): Array<BarChartProgressItem> => {
  const actual = tasks.actual ?? 0;
  const agreed = tasks.agreed ?? 0;

  return ([{
    key: 'zalando_outfit',
    label: __t('zalando_outfits_progress_widget.bar_chart_title'),
    metric: `${actual}/${agreed}`,
    actual: {
      percentage: Math.min(100, Math.round(actual * 100 / agreed)),
      metric: String(actual)
    },
    estimated: {
      percentage: 100,
      metric: String(agreed)
    },
    bgColor: COLORS.zalando_outfit.all
  }]);
};

const getStatusesStatistics = (count: ReportingOutfitsProgressCounts): Array<PieChartAlias> => ([
  {
    metric: count.unpublished,
    label: __t('zalando_outfits_progress_widget.statuses.unpublished')
  },
  {
    metric: count.approved,
    label: __t('zalando_outfits_progress_widget.statuses.approved')
  },
  {
    metric: count.offline,
    label: __t('zalando_outfits_progress_widget.statuses.offline')
  },
  {
    metric: count.pending,
    label: __t('zalando_outfits_progress_widget.statuses.pending')
  },
  {
    metric: count.published,
    label: __t('zalando_outfits_progress_widget.statuses.published')
  }
]);

const ZalandoOutfitsProgressWidget = ({
  count,
  tasks,
  isLoading
}: Props): JSX.Element => {
  const handleFormat = (params: ChartFormatterParams): string => (
    `{value|${params.value?.metric}}\n{name|${params.name}}`
  );
  const barChartItems = tasks ? getBarChartData(tasks) : [];
  const statistics = isEmpty(count)
    ? []
    : getStatusesStatistics(count as ReportingOutfitsProgressCounts);
  const pieChartData = preparePieChartData(
    statistics,
    'zaland_outfit_statuses',
    {
      formatter: handleFormat,
      colors: statistics.reduce((acc, val) => {
        if (val.metric > 0) {
          acc.push(LABEL_TO_COLOR_MAP[val.label]);
        }

        return acc;
      }, [] as Array<string>),
      styles: getReportingPieChartStyles(),
      radius: '62%'
    }
  );

  return (
    <div className="zalando-outfits-progress-widget">
      <div className="zalando-outfits-progress-widget__all-outfits">
        <BarChartProgress
          data={barChartItems}
          title={__t('zalando_outfits_progress_widget.title')}
          titleTooltip={{
            text: __t('zalando_outfits_progress_widget.title_tooltip')
          }}
          isLoading={isLoading}
          emptyMessage={__t('task_progress_widget.no_content')}
          barStyle={{ title: { marginBottom: 16 } }}
          metricContainerType={getMetricTypeWidth(barChartItems)}
        />
      </div>
      <div className="zalando-outfits-progress-widget__outfit-statuses-label">
        {__t('zalando_outfits_progress_widget.pie_chart_title')}
      </div>
      <div className="zalando-outfits-progress-widget__outfit-statuses">
        {isLoading ? (
          <div className="zalando-outfits-progress-widget__outfit-statuses-spinner">
            <Spinner
              position="inline"
              size="small"
              color="light"
            />
          </div>
        ) : (
          <PieChart
            id="zalando-outfit-statuses-pie-chart"
            hide={false}
            options={pieChartData}
            emptyMessage={__t('zalando_outfits_progress_widget.no_data')}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(
  ZalandoOutfitsProgressWidget,
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
);
